"use client";

import * as React from "react";
import { ChevronRight } from "lucide-react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";

import {
  restoreTriggerFocus,
  trackFocusModality,
} from "../../lib/restore-trigger-focus";
import { cn } from "../../lib/utils";
import { overlayItemChrome, overlayPanelChrome } from "./overlay";

/* The Sirdab dropdown menu. It does THINGS: Edit, Duplicate, Delete. The
   third leg of the disclosure split: a Select SETS A VALUE, a Filter
   NARROWS A VIEW, a menu ACTS. role=menu semantics, quiet-by-default
   rows. The ONE place quiet text is the rest state: rows sit at
   muted-foreground and rise to ink on highlight, with the ghost fill.
   Row colours SNAP (no transition class, the instant tier); the PANEL
   enters at 200ms, one step slower than pickers, because a menu is a
   destination, not a preview.

   Panel: the frosted popover treatment (overlayPanelChrome) at the menu
   radius (12px, one step softer than the picker's 10), p-1 with 2px row
   gaps.

   Marking a CURRENT choice (a language menu, a density menu) uses
   DropdownMenuRadioGroup/RadioItem: the selected row holds a STATIC
   alpha-100 fill and ink text, no check glyph; the fill IS the mark.

   The trigger is the caller's Button (ghost for icon-only "⋯" actions,
   secondary for labelled ones) via asChild. SUBMENUS: ONE level of Sub is
   allowed, for grouped preference sets (the account menu's theme and
   language). SubTrigger wears the item chrome plus a trailing chevron
   that flips in RTL; SubContent is the same frosted panel. Anything
   deeper than one level still needs a redesign, not a third tier.

   Usage:
       <DropdownMenu>
           <DropdownMenuTrigger asChild>
               <Button variant="ghost" size="icon" aria-label="خيارات">
                   <Icon icon={Ellipsis} />
               </Button>
           </DropdownMenuTrigger>
           <DropdownMenuContent>
               <DropdownMenuItem onSelect={…}>تعديل</DropdownMenuItem>
               <DropdownMenuSeparator />
               <DropdownMenuItem onSelect={…}>حذف</DropdownMenuItem>
           </DropdownMenuContent>
       </DropdownMenu>
*/

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  );
}

/* A menu is a list, and a list can outgrow the screen. Radix publishes the
   room it actually has; nothing here read it, so a menu of every section a
   store owns simply ran off the bottom edge with no way to reach the rest,
   on a desktop as much as on a phone. Select has capped itself against the
   equivalent var all along; the menu is the one that never did.

   touch-pan-y + overscroll-contain are the phone half of the same fix. A menu
   can open inside a body that vaul has pinned with `position: fixed` for its
   iOS scroll lock (the nav drawer, any tray), and iOS Safari then hands the
   drag to that pinned body rather than to the menu under the finger. */
const MENU_AVAILABLE_HEIGHT =
  "var(--radix-dropdown-menu-content-available-height)";
const menuScroller =
  "no-scrollbar touch-pan-y overflow-y-auto overscroll-contain";

function DropdownMenuContent({
  className,
  sideOffset = 4,
  onCloseAutoFocus,
  style,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  trackFocusModality();
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        /* Click-away must not leave a focus ring on the trigger; see
                   restoreTriggerFocus. Escape still rings. */
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          restoreTriggerFocus(event);
        }}
        className={cn(
          overlayPanelChrome,
          // rounded-xl overrides the panel chrome's 10px on both
          // the box and its frost pseudo. Menus are the 12px
          // tier. 200ms enter: menus are a step slower than
          // picker panels (100ms).
          "z-popover data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 flex min-w-32 flex-col gap-0.5 rounded-xl p-1 duration-200 before:rounded-xl",
          menuScroller,
          className,
        )}
        style={{ maxHeight: MENU_AVAILABLE_HEIGHT, ...style }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

/* The quiet-menu row recipe, shared by Item and RadioItem: rest at
   muted-foreground, highlight = ghost fill + ink, colours SNAP. */
const menuItemChrome = cn(
  overlayItemChrome,
  "data-highlighted:bg-btn-ghost-surface-hover text-muted-foreground data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:text-foreground [&>svg:not([class*='size-'])]:size-4",
);

function DropdownMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(menuItemChrome, className)}
      {...props}
    />
  );
}

function DropdownMenuRadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return (
    <DropdownMenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      /* The content stacks its CHILDREN at gap-0.5, but a radio group
               is one child holding many rows, so without its own gap the
               selected row's static fill welds to the hovered neighbour's. */
      className={cn("flex flex-col gap-0.5", className)}
      {...props}
    />
  );
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        menuItemChrome,
        // The current choice holds a STATIC alpha-100 fill and ink
        // text, no check glyph; the fill is the mark.
        "data-[state=checked]:bg-gray-alpha-100 data-[state=checked]:text-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn("text-label text-muted-foreground px-2 py-1", className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  );
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger>) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      className={cn(
        menuItemChrome,
        "data-[state=open]:bg-btn-ghost-surface-hover data-[state=open]:text-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ms-auto size-4 rtl:-scale-x-100" />
    </DropdownMenuPrimitive.SubTrigger>
  );
}

function DropdownMenuSubContent({
  className,
  style,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        data-slot="dropdown-menu-sub-content"
        className={cn(
          overlayPanelChrome,
          // The same 12px-tier frosted panel as the root menu.
          "z-popover data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 flex min-w-32 flex-col gap-0.5 rounded-xl p-1 duration-200 before:rounded-xl",
          menuScroller,
          className,
        )}
        style={{ maxHeight: MENU_AVAILABLE_HEIGHT, ...style }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
